import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { escapeHtml } from '../../../utils/escapeHtml';
import {
  RULER_DATE_FORMAT,
  RULER_DELTA_COLOR,
  RULER_EXCLUDED_SERIES_TYPES,
  RULER_LABEL_OFFSET,
  RULER_LINE_DASH_STYLE,
  RULER_LINE_WIDTH,
  RULER_LINE_Z_INDEX,
} from '../constants';
import {
  badgeContainerStyle,
  badgeDeltaStyle,
  badgeDotStyle,
  badgeEmptyStyle,
  badgeNameStyle,
  badgeRowStyle,
  badgeTitleStyle,
  badgeValueCellStyle,
  badgeValueStyle,
} from './chartRuler.styles';

dayjs.extend(utc);

export type XYPoint = { x: number; y: number };

type RulerLineConfig = { id: string; label: string; color: string };

type PointWithRealValue = {
  y?: unknown;
  custom?: { realValue?: unknown };
  options?: { custom?: { realValue?: unknown } };
};

/**
 * Resolves the real (pre stacked-offset) value of a point: prefers
 * `custom.realValue` — set by the backend and preserved when stacking —
 * over the displayed `y`.
 * @param point Highcharts point instance or raw data entry.
 * @returns Real numeric value, or null when the point has none.
 */
export const getRealPointValue = (point: PointWithRealValue): number | null => {
  const candidate = point?.custom?.realValue ?? point?.options?.custom?.realValue;
  if (candidate !== undefined && candidate !== null && Number.isFinite(Number(candidate))) {
    return Number(candidate);
  }
  return typeof point?.y === 'number' && Number.isFinite(point.y) ? point.y : null;
};

/**
 * Normalizes a Highcharts data entry ([x, y] tuple or point object) into an
 * x/y pair using the real (pre stacked-offset) value.
 * @param entry Raw series data entry.
 * @returns Normalized point, or null when the entry has no usable x/y.
 */
const toXYPoint = (entry: unknown): XYPoint | null => {
  if (Array.isArray(entry) && entry.length >= 2) {
    return { x: Number(entry[0]), y: Number(entry[1]) };
  }
  if (typeof entry === 'object' && entry !== null && 'x' in entry && 'y' in entry) {
    const point = entry as { x: unknown } & PointWithRealValue;
    return { x: Number(point.x), y: Number(getRealPointValue(point) ?? point.y) };
  }
  return null;
};

/**
 * Interpolates the Y value at a given x over a set of points, clamping to the
 * first/last values outside the data range.
 * @param rawPoints Points to evaluate; non-finite entries are ignored.
 * @param targetX X value (timestamp in ms) to evaluate.
 * @returns Interpolated Y value, or null when there are no valid points.
 */
export const interpolateYAtX = (rawPoints: XYPoint[], targetX: number): number | null => {
  const points = rawPoints
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);
  if (points.length === 0) return null;

  const first = points[0];
  const last = points[points.length - 1];
  if (targetX <= first.x) return first.y;
  if (targetX >= last.x) return last.y;

  const upperIndex = points.findIndex((p) => p.x >= targetX);
  const lower = points[upperIndex - 1];
  const upper = points[upperIndex];
  const ratio = (targetX - lower.x) / (upper.x - lower.x);
  return lower.y + ratio * (upper.y - lower.y);
};

/**
 * Finds the real Y value of a series at a given x, interpolating linearly
 * between the two surrounding points and clamping outside the data range.
 * @param serie Highcharts series instance.
 * @param targetX Timestamp (ms) to evaluate.
 * @returns Interpolated Y value, or null when the series has no valid data.
 */
export const getSeriesYAtX = (serie: any, targetX: number): number | null => {
  const data: unknown[] = serie.options?.data || serie.userOptions?.data || [];
  const points = data
    .map(toXYPoint)
    .filter((p): p is XYPoint => p !== null);
  return interpolateYAtX(points, targetX);
};

/**
 * Whether a series holds plottable data for the ruler badge. Bands
 * (arearange) and the comments series (scatter) are excluded.
 * @param serie Highcharts series instance.
 * @returns True when the series should appear in the badge.
 */
const isDataSeries = (serie: any): boolean => {
  const type = serie?.userOptions?.type || serie?.type;
  return Boolean(serie) && serie.visible !== false && !RULER_EXCLUDED_SERIES_TYPES.includes(type);
};

/**
 * Builds the HTML for the delta between both rulers, colored by sign.
 * @param delta Difference between the current and the compared value.
 * @returns HTML string for the delta span.
 */
const buildDeltaHTML = (delta: number): string => {
  const colorBySign = delta > 0 ? RULER_DELTA_COLOR.positive : RULER_DELTA_COLOR.negative;
  const color = delta === 0 ? RULER_DELTA_COLOR.neutral : colorBySign;
  const sign = delta > 0 ? '+' : '';
  return `<span style="${badgeDeltaStyle(color)}">${sign}${delta.toFixed(2)}</span>`;
};

/**
 * Builds one badge row with the series name, its value at the ruler and,
 * when a comparison x is given, the delta against that point.
 * @param serie Highcharts series instance.
 * @param targetX Ruler timestamp (ms).
 * @param compareToX Optional timestamp (ms) of the other ruler.
 * @returns HTML string for the row, or null when the series has no value.
 */
const buildBadgeRowHTML = (serie: any, targetX: number, compareToX?: number | null)
: string | null => {
  const y = getSeriesYAtX(serie, targetX);
  if (y === null || !Number.isFinite(y)) return null;
  const name = escapeHtml(serie.userOptions?.name || serie.name || 'Serie');
  const color = serie.userOptions?.color || serie.color || '#FFFFFF';
  const yPrev = compareToX != null ? getSeriesYAtX(serie, compareToX) : null;
  const deltaHtml = yPrev !== null && Number.isFinite(yPrev) ? buildDeltaHTML(y - yPrev) : '';
  return `<div style="${badgeRowStyle}">
    <span style="${badgeNameStyle}"><span style="${badgeDotStyle(color)}"></span>${name}</span>
    <span style="${badgeValueCellStyle}"><span style="${badgeValueStyle}">${y.toFixed(2)}</span>${deltaHtml}</span>
  </div>`;
};

/**
 * Builds the badge HTML for a ruler: title with label and UTC date (matching
 * the tooltip format) plus one row per visible data series.
 * @param chart Highcharts chart instance.
 * @param targetX Ruler timestamp (ms).
 * @param label Ruler display label.
 * @param bgColor Badge background color.
 * @param compareToX Optional timestamp (ms) of the other ruler, for deltas.
 * @returns HTML string for the plot line label.
 */
export const buildRulerBadgeHTML = (
  chart: any,
  targetX: number,
  label: string,
  bgColor: string,
  compareToX?: number | null,
): string => {
  const dateStr = dayjs.utc(targetX).format(RULER_DATE_FORMAT);
  const rows = (chart.series || [])
    .filter(isDataSeries)
    .map((serie: any) => buildBadgeRowHTML(serie, targetX, compareToX))
    .filter(Boolean)
    .join('');
  return `<div style="${badgeContainerStyle(bgColor, compareToX != null)}">
    <div style="${badgeTitleStyle}">${label} · ${dateStr}</div>
    ${rows || `<div style="${badgeEmptyStyle}">Sin valores</div>`}
  </div>`;
};

/**
 * Builds the Highcharts plot line options for a ruler.
 * @param line Ruler line config (id, label, color).
 * @param value Ruler timestamp (ms).
 * @param badgeHTML Pre-built badge HTML for the label.
 * @returns Plot line options object for `Axis.addPlotLine`.
 */
export const buildRulerPlotLine = (line: RulerLineConfig, value: number, badgeHTML: string) => ({
  id: line.id,
  value,
  color: line.color,
  width: RULER_LINE_WIDTH,
  zIndex: RULER_LINE_Z_INDEX,
  dashStyle: RULER_LINE_DASH_STYLE,
  label: {
    text: badgeHTML,
    align: 'left',
    verticalAlign: 'top',
    rotation: 0,
    x: RULER_LABEL_OFFSET.x,
    y: RULER_LABEL_OFFSET.y,
    useHTML: true,
  },
});
